import type { TFunction } from "i18next";
import {
  ContractTaskResponsiblePartyDto,
  ContractTaskStatusDto,
  type ContractTaskDto,
  type PersonDto,
} from "../../../autogen/bff-api";
import type { ExportCell, ExportColumn } from "../../../common/download/listExport";
import { displayDate } from "../../../common/formatting/displayDate";
import { displayPersonName } from "../../../common/persons/displayPersonName";

export const taskListColumns = (t: TFunction): ExportColumn[] => [
  { key: "title", label: String(t("Title")) },
  { key: "dueDate", label: String(t("Due date")) },
  { key: "responsible", label: String(t("Responsible")) },
  { key: "contract", label: String(t("Contract")) },
  { key: "owner", label: String(t("Contract owner")) },
  { key: "status", label: String(t("Status")) },
];

export const dueBeforeLabel = (t: TFunction, date: string): string =>
  String(t("Due before {{date}}", { date: displayDate(date) }));

export const changedSinceLabel = (t: TFunction, date: string): string =>
  String(t("Changed since {{date}}", { date: displayDate(date) }));

export interface TaskListFilters {
  statuses: { label: string }[];
  responsiblePersons: PersonDto[];
  contracts: { label: string }[];
  dueBefore?: string;
  changedSince?: string;
}

export const describeTaskFilters = (t: TFunction, filters: TaskListFilters): string[] =>
  [
    filters.statuses.length > 0 ? `${t("Status")}: ${filters.statuses.map((s) => s.label).join(", ")}` : null,
    filters.responsiblePersons.length > 0
      ? `${t("Responsible")}: ${filters.responsiblePersons.map(displayPersonName).join(", ")}`
      : null,
    filters.contracts.length > 0 ? `${t("Contract")}: ${filters.contracts.map((c) => c.label).join(", ")}` : null,
    filters.dueBefore ? dueBeforeLabel(t, filters.dueBefore) : null,
    filters.changedSince ? changedSinceLabel(t, filters.changedSince) : null,
  ].filter((filter): filter is string => filter !== null);

export const taskCell = (
  key: string,
  task: ContractTaskDto,
  ctx: { t: TFunction; selectedOrgId: string },
): ExportCell => {
  switch (key) {
    case "title":
      return { text: task.title };
    case "dueDate":
      return { date: task.dueDate };
    case "responsible":
      return {
        text:
          task.responsibleParty === ContractTaskResponsiblePartyDto.Organization
            ? task.responsibleOrganization.name
            : displayPersonName(task.responsiblePerson).trim(),
      };
    case "contract":
      return { text: task.contractTitle, contractId: task.contractId };
    case "owner":
      return {
        text: ctx.selectedOrgId === task.owningOrganizationId ? String(ctx.t("Current org")) : task.owningOrganizationName,
      };
    case "status":
      return { text: statusLabel(ctx.t, task.displayStatus) };
    default:
      return null;
  }
};

const statusLabel = (t: TFunction, status: ContractTaskStatusDto): string =>
  ({
    [ContractTaskStatusDto.Open]: t("Open"),
    [ContractTaskStatusDto.Completed]: t("Completed"),
    [ContractTaskStatusDto.Overdue]: t("Overdue"),
  })[status];

export const taskListFileName = (now: Date): string => `Kontraktsoppgaver-${now.toLocaleDateString("sv-SE")}`;
